import type { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type {
  IApiUpdateLegalDocumentRequestBody,
  IApiUpdateLegalDocumentResponseBody,
} from '~/lib/api/modules/LegalDocuments';
import type { IUseUpdateLegalDocumentMutationValues } from '~/lib/api/modules/LegalDocuments/hooks/useUpdateLegalDocumentMutation';
import type { ILegalDocumentFormValues } from './LegalDocumentForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { markdownToHtml } from '~/helpers/markdownToHtml';
import { useLegalDocumentQuery } from '~/lib/api/modules/LegalDocuments/hooks/useLegalDocumentQuery';
import { useUpdateLegalDocumentMutation } from '~/lib/api/modules/LegalDocuments/hooks/useUpdateLegalDocumentMutation';
import { LegalDocumentForm } from './LegalDocumentForm';

export type IUpdateLegalDocumentFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateLegalDocumentResponseBody,
    inputValues: IUseUpdateLegalDocumentMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  legalDocumentId: string;
};

export const UpdateLegalDocumentForm: React.FC<
  IUpdateLegalDocumentFormProps
> = (props) => {
  const { sx, onSuccess, cancelUrl, appId, legalDocumentId } = props;
  const legalDocumentQuery = useLegalDocumentQuery({ appId, legalDocumentId });
  const updateLegalDocumentMutation = useUpdateLegalDocumentMutation();
  const { t } = useTranslation();
  const legalDocument = legalDocumentQuery.data;

  const handleSubmit: SubmitHandler<ILegalDocumentFormValues> = (values) => {
    if (updateLegalDocumentMutation.isLoading) {
      return;
    }

    return markdownToHtml(values.markdown!).then((html) => {
      const body: IApiUpdateLegalDocumentRequestBody = {
        type: values.type!,
        markdown: values.markdown!,
        html,
        title: values.title!,
        outdated: !!values.outdated,
        required: values.required,
      };

      return updateLegalDocumentMutation.mutateAsync(
        { appId, legalDocumentId, body },
        { onSuccess },
      );
    });
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title>
        {t('UpdateLegalDocumentForm.title')}
      </ContentBlock.Title>
      <ContentBlock.Suspense
        loading={legalDocumentQuery.isLoading}
        error={legalDocumentQuery.error}
      >
        {legalDocument ? (
          <LegalDocumentForm
            mode="edition"
            cancelUrl={cancelUrl}
            defaultValues={{
              type: legalDocument.type,
              title: legalDocument.title,
              markdown: legalDocument.markdown,
              outdated: legalDocument.outdated,
              required: legalDocument.required,
            }}
            onSubmit={handleSubmit}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
